import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { CreditCard, Building2, Loader2, CheckCircle2, XCircle } from 'lucide-react';

interface PaymentFormProps {
  accountId?: number;
  accountName?: string;
  defaultAmount?: number;
  onSuccess?: (result: PaymentResult) => void;
  onCancel?: () => void;
}

interface PaymentResult {
  success: boolean;
  transactionId?: string;
  authCode?: string;
  amount?: number;
  message?: string;
}

export function PaymentForm({ 
  accountId, 
  accountName, 
  defaultAmount = 0,
  onSuccess,
  onCancel 
}: PaymentFormProps) {
  const [paymentType, setPaymentType] = useState<'card' | 'ach'>('card');
  const [isRecurring, setIsRecurring] = useState(false);
  const [result, setResult] = useState<PaymentResult | null>(null);
  
  // Form state
  const [amount, setAmount] = useState(defaultAmount.toString());
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  
  // Card fields
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  
  // ACH fields
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<'CHECKING' | 'SAVINGS'>('CHECKING');
  
  // Recurring fields
  const [startDate, setStartDate] = useState('');
  const [frequency, setFrequency] = useState<'W' | 'B' | 'M' | 'Q'>('M');
  const [payments, setPayments] = useState('12');
  
  const cardPayment = trpc.payments.processCardPayment.useMutation();
  const achPayment = trpc.payments.processACHPayment.useMutation();
  const recurringPayment = trpc.payments.setupRecurringPayment.useMutation();
  
  const isLoading = cardPayment.isPending || achPayment.isPending || recurringPayment.isPending;
  
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : digits;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    
    const customer = {
      firstName,
      lastName,
      email: email || undefined,
      address1: address || undefined,
      city: city || undefined,
      state: state || undefined,
      zip: zip || undefined,
      primaryPhone: phone || undefined,
    };
    
    try {
      let response: PaymentResult;
      
      if (isRecurring) {
        // Setup recurring payment
        const recurring = {
          startOn: startDate,
          frequency,
          payments: parseInt(payments),
        };
        
        if (paymentType === 'card') {
          response = await recurringPayment.mutateAsync({
            amount: parseFloat(amount),
            customer,
            card: {
              cardNumber: cardNumber.replace(/\s/g, ''),
              expMonth,
              expYear,
              cvv,
            },
            recurring,
          });
        } else {
          response = await recurringPayment.mutateAsync({
            amount: parseFloat(amount),
            customer,
            ach: {
              routingNumber,
              accountNumber,
              accountType,
            },
            recurring,
          });
        }
      } else {
        // One-time payment
        if (paymentType === 'card') {
          response = await cardPayment.mutateAsync({
            amount: parseFloat(amount),
            customer,
            card: {
              cardNumber: cardNumber.replace(/\s/g, ''),
              expMonth,
              expYear,
              cvv,
            },
          });
        } else {
          response = await achPayment.mutateAsync({
            amount: parseFloat(amount),
            customer,
            ach: {
              routingNumber,
              accountNumber,
              accountType,
            },
          });
        }
      }
      
      setResult(response);
      if (response.success && onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : 'Payment failed',
      });
    }
  };
  
  // Show result screen
  if (result) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            {result.success ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
                <h3 className="text-xl font-semibold text-green-500">Payment Successful</h3>
                <div className="text-muted-foreground space-y-1">
                  <p>Transaction ID: {result.transactionId}</p>
                  {result.authCode && <p>Auth Code: {result.authCode}</p>}
                  <p>Amount: ${result.amount?.toFixed(2)}</p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                <h3 className="text-xl font-semibold text-red-500">Payment Failed</h3>
                <p className="text-muted-foreground">{result.message}</p>
              </>
            )}
            <div className="flex gap-2 justify-center pt-4">
              <Button variant="outline" onClick={() => setResult(null)}>
                {result.success ? 'New Payment' : 'Try Again'}
              </Button>
              {onCancel && (
                <Button onClick={onCancel}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="w-full max-w-lg mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Process Payment
        </CardTitle>
        {accountName && (
          <CardDescription>
            Payment for: {accountName}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Payment Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Payment Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
                required
              />
            </div>
          </div>
          
          {/* Payment Type Tabs */}
          <Tabs value={paymentType} onValueChange={(v) => setPaymentType(v as 'card' | 'ach')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="card" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Credit Card
              </TabsTrigger>
              <TabsTrigger value="ach" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Bank Account
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="card" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="cardNumber">Card Number</Label>
                <Input
                  id="cardNumber"
                  placeholder="4111 1111 1111 1111"
                  value={formatCardNumber(cardNumber)}
                  onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                  maxLength={19}
                  required={paymentType === 'card'}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expMonth">Month</Label>
                  <Input
                    id="expMonth"
                    placeholder="MM"
                    value={expMonth}
                    onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    maxLength={2}
                    required={paymentType === 'card'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expYear">Year</Label>
                  <Input
                    id="expYear"
                    placeholder="YY"
                    value={expYear}
                    onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    maxLength={2}
                    required={paymentType === 'card'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cvv">CVV</Label>
                  <Input
                    id="cvv"
                    placeholder="123"
                    type="password"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4}
                    required={paymentType === 'card'}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="ach" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="accountType">Account Type</Label>
                <Select value={accountType} onValueChange={(v) => setAccountType(v as 'CHECKING' | 'SAVINGS')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECKING">Checking</SelectItem>
                    <SelectItem value="SAVINGS">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="routingNumber">Routing Number</Label>
                <Input
                  id="routingNumber"
                  placeholder="123456789"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  maxLength={9}
                  required={paymentType === 'ach'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number</Label>
                <Input
                  id="accountNumber"
                  placeholder="Account Number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                  required={paymentType === 'ach'}
                />
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Customer Information */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Customer Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address (Optional)</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  maxLength={5}
                />
              </div>
            </div>
          </div>
          
          {/* Recurring Payment Option */}
          <div className="flex items-center space-x-2 pt-4 border-t">
            <Checkbox
              id="recurring"
              checked={isRecurring}
              onCheckedChange={(checked) => setIsRecurring(checked === true)}
            />
            <Label htmlFor="recurring" className="cursor-pointer">
              Set up recurring payment plan
            </Label>
          </div>
          
          {isRecurring && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium">Payment Plan Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required={isRecurring}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select value={frequency} onValueChange={(v) => setFrequency(v as 'W' | 'B' | 'M' | 'Q')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="W">Weekly</SelectItem>
                      <SelectItem value="B">Bi-Weekly</SelectItem>
                      <SelectItem value="M">Monthly</SelectItem>
                      <SelectItem value="Q">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payments">Number of Payments</Label>
                <Input
                  id="payments"
                  type="number"
                  min="1"
                  max="999"
                  value={payments}
                  onChange={(e) => setPayments(e.target.value)}
                  required={isRecurring}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Total: ${(parseFloat(amount || '0') * parseInt(payments || '0')).toFixed(2)} over {payments} payments
              </p>
            </div>
          )}
          
          {/* Submit Buttons */}
          <div className="flex gap-2 pt-4">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Process ${parseFloat(amount || '0').toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default PaymentForm;
